import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    NewReleasePayloadType,
    tryNewReleaseAction,
    tryUpdateReleaseEntity,
    TRY_NEW_RELEASE,
    TRY_UPDATE_RELEASE_ENTITY,
    UpdateReleasePayloadType,
    NewReleaseRequest,
    UpdateReleaseRequest,
    CreateHotfixOrRollbackReleaseRequest,
} from '../../actions/release';
import { RootState } from '../../reducers/reducers';
import { getError, getIsLoading, getUpdated } from '../../helpers';
import { AppRouteComponentMatchParams } from '../../typescriptHelpers';

export interface UseReleaseCreationProps {
    orgId: string;
    appId: string;
    releaseId?: string;
}

export interface UseReleaseCreationReturn {
    isCreatingRelease: boolean;
    createReleaseSuccess: boolean;
    createReleaseError: string | null;

    isUpdatingRelease: boolean;
    updateReleaseSuccess: boolean;
    updateReleaseError: string | null;

    isLoading: boolean;

    createNewRelease: (request: NewReleaseRequest | CreateHotfixOrRollbackReleaseRequest) => void;
    createNewReleaseWithConversion: (
        request: NewReleaseRequest | CreateHotfixOrRollbackReleaseRequest,
        isRollbackConversion: boolean,
        conversionReleaseId?: string,
    ) => void;
    updateRelease: (request: UpdateReleaseRequest) => void;
}

export const useReleaseCreation = ({ orgId, appId, releaseId }: UseReleaseCreationProps): UseReleaseCreationReturn => {
    const dispatch = useDispatch();
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const isCreatingRelease = useSelector((state: RootState) => getIsLoading(TRY_NEW_RELEASE, state, releaseId));
    const createReleaseSuccess = useSelector((state: RootState) => getUpdated(TRY_NEW_RELEASE, state, releaseId));
    const createReleaseError = useSelector((state: RootState) => getError(TRY_NEW_RELEASE, state, releaseId));

    const isUpdatingRelease = useSelector((state: RootState) =>
        getIsLoading(TRY_UPDATE_RELEASE_ENTITY, state, releaseId),
    );
    const updateReleaseSuccess = useSelector((state: RootState) =>
        getUpdated(TRY_UPDATE_RELEASE_ENTITY, state, releaseId),
    );
    const updateReleaseError = useSelector((state: RootState) => getError(TRY_UPDATE_RELEASE_ENTITY, state, releaseId));

    const isLoading = isCreatingRelease || isUpdatingRelease;

    const createNewRelease = (request: NewReleaseRequest | CreateHotfixOrRollbackReleaseRequest) => {
        const payload: NewReleasePayloadType = {
            orgId,
            orgIdParam,
            appId,
            appIdParam,
            request,
            isRollbackConversion: false,
        };

        dispatch(tryNewReleaseAction(payload));
    };

    const createNewReleaseWithConversion = (
        request: NewReleaseRequest | CreateHotfixOrRollbackReleaseRequest,
        isRollbackConversion: boolean,
        conversionReleaseId?: string,
    ) => {
        const payload: NewReleasePayloadType = {
            orgId,
            orgIdParam,
            appId,
            appIdParam,
            request,
            isRollbackConversion,
            releaseId: conversionReleaseId,
        };

        dispatch(tryNewReleaseAction(payload));
    };

    const updateRelease = (request: UpdateReleaseRequest) => {
        const payload: UpdateReleasePayloadType = {
            orgId,
            orgIdParam,
            appId,
            appIdParam,
            request,
        };

        dispatch(tryUpdateReleaseEntity(payload));
    };

    return {
        isCreatingRelease,
        createReleaseSuccess,
        createReleaseError,
        isUpdatingRelease,
        updateReleaseSuccess,
        updateReleaseError,
        isLoading,
        createNewRelease,
        createNewReleaseWithConversion,
        updateRelease,
    };
};
